import downloadjs from 'downloadjs'
import moment from 'moment'
import numeral from 'numeral'
import { PseudoFile } from 'src/app/utils.service'
import { CsvJson } from './CsvJson.model'

export class Csv {
    // private _file: string | undefined;
    json: CsvJson = { headers: [], rows: [] }
    private _fileUrl: string
    private _id: string

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static toText(csv: CsvJson) {
        const joinedHeaders: string = csv.headers.join(',')
        const joinedRows: string = csv.rows.map((row) => row.join(',')).join('\r\n')
        const text = `${joinedHeaders}\r\n${joinedRows}`
        return text
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static findLastCellIndexWithValue(csv: CsvJson, headerIndex: number) {
        for (let i = 0; i < csv.rows.length; i++) {
            if (!csv.rows[i][headerIndex]) {
                return i - 1
            }
        }
        return -1
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static sort(csv: CsvJson, header: string, direction: 'asc' | 'des', dataType?: 'text' | 'number' | 'date') {
        const headerIndex = csv.headers.indexOf(header)
        // console.log('[ Csv.model ] - sort - headerIndex', headerIndex);
        if (headerIndex === -1) {
            return
        }

        const sortAscending = direction === 'asc'

        csv.rows.sort((rowA, rowB) => {
            if (dataType === 'date') {
                const valueA = moment(rowA[headerIndex], 'M/D/Y')
                const valueB = moment(rowB[headerIndex], 'M/D/Y')
                const difference = valueA.diff(valueB)

                if (difference > 0) {
                    return sortAscending ? 1 : -1
                }

                if (difference < 0) {
                    return sortAscending ? -1 : 1
                }

                return 0
            } else {
                const valueA =
                    dataType === 'number' ? numeral(Csv.getComparableCellValue(rowA[headerIndex])).value() : Csv.getComparableCellValue(rowA[headerIndex])
                const valueB =
                    dataType === 'number' ? numeral(Csv.getComparableCellValue(rowB[headerIndex])).value() : Csv.getComparableCellValue(rowB[headerIndex])

                if (valueA > valueB) {
                    return sortAscending ? 1 : -1
                }

                if (valueA < valueB) {
                    return sortAscending ? -1 : 1
                }

                return 0
            }
        })
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static generateCutAndStack(csv: CsvJson, stackColumnIndex: number) {
        // this.rows.splice(0, this.rows.length - 20);
        const headers = csv.headers.map((value) => {
            return value.toLowerCase()
        })
        const currentSequenceHeader = headers.indexOf('sequence')
        if (currentSequenceHeader === -1) {
            throw new Error('A Csv must have a sequence column for cut & stack')
        }
        if (stackColumnIndex < 0) {
            throw new Error('The stackColumnIndex cannot be less than 0')
        }
        if (stackColumnIndex > csv.headers.length) {
            throw new Error('The stackColumnIndex cannot be greater than the number of columns')
        }
        Csv.sort(csv, 'sequence', 'des', 'number')
        const stackHeaderIndex = stackColumnIndex
        Csv.addColumn(csv, 'stack', stackHeaderIndex)
        const newSequenceHeader = csv.headers
            .map((value) => {
                return value.toLowerCase()
            })
            .indexOf('sequence')
        let x = csv.rows.length
        const recordsPerSheet = 4

        const y = x % recordsPerSheet
        for (let i = y; i < recordsPerSheet; i++) {
            const columns = csv.headers.length
            const newRow = new Array(columns).map(() => '')
            newRow[newSequenceHeader] = `${csv.rows.length + 1}`
            csv.rows.unshift(newRow)
        }
        const z = (x + recordsPerSheet - y) / recordsPerSheet
        x = csv.rows.length

        for (let j = 1; j <= recordsPerSheet; j++) {
            for (let k = 1; k <= z; k++) {
                if (k !== 1) {
                    const nextEmptyCellIndex = Csv.findLastCellIndexWithValue(csv, stackHeaderIndex) + 1
                    const previousCellValue = parseInt(csv.rows[nextEmptyCellIndex - 1][stackHeaderIndex]) - 2
                    csv.rows[nextEmptyCellIndex][stackHeaderIndex] = `${previousCellValue}`
                } else {
                    if (j === 1) {
                        csv.rows[0][stackHeaderIndex] = `${x}`
                    } else {
                        const nextEmptyCellIndex = Csv.findLastCellIndexWithValue(csv, stackHeaderIndex) + 1

                        if (j === 2) {
                            csv.rows[nextEmptyCellIndex][stackHeaderIndex] = `${x - 1}`
                        } else if (j === 3) {
                            csv.rows[nextEmptyCellIndex][stackHeaderIndex] = `${x / 2}`
                        } else {
                            csv.rows[nextEmptyCellIndex][stackHeaderIndex] = `${x / 2 - 1}`
                        }
                    }
                }
            }
        }

        Csv.sort(csv, 'stack', 'des', 'number')
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static addColumn(csv: CsvJson, name: string, index: number) {
        csv.headers.splice(index, 0, name)
        csv.rows.forEach((row) => {
            row.splice(index, 0, '')
        })
    }
    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static removeColumn(csv: CsvJson, column: string) {
        const columnIndex = csv.headers.indexOf(column)
        const hasColumn = columnIndex !== -1

        if (!hasColumn) {
            return
        }

        csv.headers.splice(columnIndex, 1)
        csv.rows.forEach((row) => {
            row.splice(columnIndex, 1)
        })
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static getComparableCellValue(cellValue: string): string {
        if (typeof cellValue !== 'string') {
            return ''
        }

        let freshCellValue = `${cellValue}`.toUpperCase()
        freshCellValue = freshCellValue.replace(/[^A-Z0-9- ]/g, '')

        return freshCellValue.trim()
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static mergeHeadersAndRows(files: CsvJson[]): CsvJson {
        const allHeaders: string[] = []
        const allRows: string[][] = []

        if (!files) {
            return {
                headers: allHeaders,
                rows: allRows,
            }
        }

        for (const file of files) {
            for (const header of file.headers) {
                if (allHeaders.indexOf(header) === -1) {
                    allHeaders.push(header)
                }
            }
        }

        for (const file of files) {
            const { headers, rows } = file

            rows.forEach((row) => {
                const isEmptyRow: boolean = row.length === 1 && !row[0]

                if (isEmptyRow) {
                    return
                }

                const newRow: string[] = []
                headers.forEach((header, headerIndex) => {
                    const allHeaderIndex: number = allHeaders.indexOf(header)

                    const cellValue: string = row[headerIndex]
                    newRow[allHeaderIndex] = cellValue
                })

                allRows.push(newRow)
            })
        }

        for (let i = 0; i < allHeaders.length; i++) {
            allRows.forEach((row) => {
                if (row[i] === undefined) {
                    row[i] = ''
                }
            })
        }

        return {
            headers: allHeaders,
            rows: allRows,
        }
    }

    static toFile(csv: CsvJson, fileName: string): PseudoFile {
        const csvAsText = Csv.toText(csv)
        const file = {
            name: fileName,
            blob: new Blob([csvAsText], { type: 'text/csv' }),
        }
        return file
    }

    static async readFileAsText(file: File, minRows: number): Promise<{ isValid: true; csv: Csv } | { isValid: false; message: string }> {
        if (!file.name.endsWith('.csv')) {
            const message = `Please upload a .csv file only. You can create a .csv file by using the "Save As" functionality and selecting "Comma Separated Values" in Excel.`
            return {
                isValid: false,
                message,
            }
        }

        const reader = new FileReader()
        reader.readAsText(file)

        return new Promise<{ isValid: true; csv: Csv } | { isValid: false; message: string }>((resolve, reject) => {
            reader.onload = () => {
                try {
                    const result: string | ArrayBuffer | null = reader.result
                    if (typeof result !== 'string') {
                        return resolve({ isValid: false, message: 'No Data Found in file' })
                    }
                    const csv = new Csv('', result, '')
                    if (csv.rows.length < minRows) {
                        return resolve({ isValid: false, message: `File should have at least ${minRows} rows` })
                    }
                    if (csv.headers.length < 1) {
                        return resolve({ isValid: false, message: 'No Headers Found in file' })
                    }
                    resolve({ isValid: true, csv })
                } catch (err) {
                    reject(err)
                }
            }
        })
    }

    static createFromFile(file: File, minRows: number) {
        return this.readFileAsText(file, minRows)
    }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    static validateRequiredHeaders(headers: string[], requiredHeaders: string[]): { isValid: false; message: string } | { isValid: true } {
        const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header))

        if (missingHeaders.length > 0) {
            return { isValid: false, message: `Missing required headers: ${missingHeaders.join(', ')}` }
        }

        return { isValid: true }
    }

    constructor(id: string, csvText: string, fileUrl: string) {
        // this.file = csvText;
        this.populateHeadersAndRows(csvText)
        this._fileUrl = fileUrl
        this._id = id
    }

    get id() {
        return this._id
    }

    get headers() {
        return this.json.headers
    }

    get rows() {
        return this.json.rows
    }

    get fileUrl() {
        return this._fileUrl
    }

    // get file() {
    //     return this._file;
    // }

    // set file(file) {
    //     this._file = file;
    //     this.populateHeadersAndRows();
    // }

    // Should be the same as /server/src/controllers/parse/csv-utils.ts
    private splitByCommas(commaSeperatedValues: string): string[] {
        const split = []
        const SEPERATOR = ','
        const DOUBLE_QUOTES = '"'
        let start = 0
        let inDoubleQuotes = false
        for (let i = 0; i < commaSeperatedValues.length; i++) {
            const value = commaSeperatedValues[i]
            if (value === DOUBLE_QUOTES) {
                inDoubleQuotes = !inDoubleQuotes
            }

            if (value === SEPERATOR && !inDoubleQuotes) {
                split.push(commaSeperatedValues.substring(start, i))
                start = i + 1
            }
        }

        split.push(commaSeperatedValues.substring(start))
        return split
    }

    private populateHeadersAndRows(csvText: string) {
        // console.log('[ CSV.model ] - populateHeadersAndRows()', csvText);
        if (!csvText) {
            return this
        }

        // this.file
        const csvSplitByEndLine = csvText.split(/\r\n|\r|\n/g)
        // console.log('[ CSV.model ] - populateHeadersAndRows() - csvSplitByEndLine', csvSplitByEndLine);
        // console.log('[ CSV.model ] - populateHeadersAndRows() - csvSplitByEndLine.length', csvSplitByEndLine.length);

        const headers = this.splitByCommas(csvSplitByEndLine[0]).map((header) => {
            return header.trim()
        })
        // console.log('[ CSV.model ] - populateHeadersAndRows() - headers', headers);

        const rows = []
        for (let i = 1; i < csvSplitByEndLine.length; i++) {
            rows.push(this.splitByCommas(csvSplitByEndLine[i]))
        }

        for (let i = headers.length - 1; i > -1; i--) {
            if (headers[i] === '') {
                for (const row of rows) {
                    row.splice(i, 1)
                }
                headers.splice(i, 1)
            }
        }

        // console.log('[ CSV.model ] - populateHeadersAndRows() - headers 2', headers);

        if (rows[rows.length - 1].length === 1 && !rows[rows.length - 1][0]) {
            rows.splice(rows.length - 1, 1)
        }

        this.json = { headers, rows: headers.length === 0 ? [] : rows }
    }

    toFile(fileName: string): PseudoFile {
        return Csv.toFile(this, fileName)
    }

    toText(): string {
        return Csv.toText(this)
    }

    addRow(row: { [key: string]: string }, where: 'beginning' | 'end') {
        const keys = Object.keys(row)
        const newRow: string[] = []
        for (const key of keys) {
            let headerIndex = this.headers.indexOf(key)
            if (headerIndex === -1) {
                this.headers.push(key)
                headerIndex = this.headers.length - 1
            }
            newRow[headerIndex] = row[key]
        }

        if (where === 'end') {
            this.rows.push(newRow)
        } else if (where === 'beginning') {
            this.rows.unshift(newRow)
        }
    }

    updateRow(row: { [key: string]: string }, rowIndex: number) {
        const keys = Object.keys(row)
        const newRow: string[] = []
        for (const key of keys) {
            let headerIndex = this.headers.indexOf(key)
            if (headerIndex === -1) {
                this.headers.push(key)
                headerIndex = this.headers.length - 1
            }
            newRow[headerIndex] = row[key]
        }
        this.rows[rowIndex] = newRow
    }
    deleteRow(rowIndex: number) {
        this.rows.splice(rowIndex, 1)
    }

    download(fileName: string) {
        downloadjs(this.toFile(fileName).blob, fileName, 'text/csv')
    }
}
